import {EnumStatusPedido} from '../../../server/lib/emun/EnumStatusPedido';

/**
 * Regras das comandas da home-celular, sem Angular para poder ter spec.
 *
 * Os pedidos vêm da lista de pedidos (DTOPedido): `status` é o RÓTULO ("Novo"),
 * o número vem em `statusOrdem`, e `aceito` é o campo real (a loja aceita sozinha
 * por padrão; só pede aceite com o aceite automático desligado ou integração).
 *
 * Quais status podem ser escolhidos, quando perguntar "Foi pago?" e o que vale
 * como entrega copiam o `mudar-status` (componentes/mudar-status): a home faz
 * em um toque exatamente o que a tela de pedidos faz hoje.
 */

export type EtapaComanda = 'aceitar' | 'pix' | 'novo' | 'preparo' | 'pronto' | 'rua' | 'receber';

export type AbaComanda = 'todos' | 'aceitar' | 'pix' | 'preparo' | 'prontos' | 'rua' | 'receber';

/** Rótulo do status (o que a API de status e o mudar-status usam) → código. */
export const CODIGO_DO_STATUS: {[rotulo: string]: number} = {
  'Em preparação': EnumStatusPedido.EmPreparacao,
  'Pronto': EnumStatusPedido.Pronto,
  'Saiu para entrega': EnumStatusPedido.SaiuParaEntrega,
  'Entregue': EnumStatusPedido.Entregue
};

/** O que o botão principal da comanda faz. */
export interface PassoComanda {
  tipo: 'aceitar' | 'status' | 'pagar';
  status?: string;
  texto: string;
  icone: string;
}

const ORDEM_DAS_ETAPAS: EtapaComanda[] = ['aceitar', 'pix', 'novo', 'preparo', 'pronto', 'rua', 'receber'];

/** Mesma regra do `atualizarFoiPago` do mudar-status: quando a tela pergunta "Foi pago?". */
export function atualizaFoiPago(pedido: any): boolean {
  return !pedido.reposicaoInterna && !pedido.fiado &&
    (!pedido.pago || !pedido.pontosGanhos) &&
    (!pedido.pagamentoOnline || !!pedido.pagamentoParcial);
}

/** Mesma regra do `pedidoPodeSerAlterado` do mudar-status. */
export function podeSerAlterado(pedido: any): boolean {
  if (pedido.aguardandoPagamentoOnline) return false;
  if (pedido.comanda && !pedido.comanda.aberta) return false;
  if (pedido.agendadoParaOutroDia) return false;
  return true;
}

/** Entrega para o mudar-status: tem forma de entrega e ela não é "Retirar". */
export function ehEntrega(pedido: any): boolean {
  return !!pedido.formaDeEntrega && pedido.formaDeEntrega !== 'Retirar';
}

/**
 * Status para onde o pedido pode ir — `obtenhaNovosStatus` do mudar-status sem
 * Cancelado (cancelar pede motivo e continua no detalhe do pedido).
 */
export function statusPermitidos(pedido: any, podeAlterarStatus: boolean): string[] {
  if (pedido.naoAlterarStatus || !podeAlterarStatus || !podeSerAlterado(pedido)) return [];

  const permitidos = ['Em preparação', 'Pronto'];
  const externo = pedido.externo != null; // iFood, Keeta, 99Food...

  if (!externo || pedido.externo.entregaPelaLoja || (pedido.do99food && pedido.retirar)) {
    if (ehEntrega(pedido)) permitidos.push('Saiu para entrega');
    permitidos.push('Entregue');
  }

  return permitidos;
}

/** Em que ponto da fila o pedido está; null = não entra no trilho. */
export function etapaDaComanda(pedido: any): EtapaComanda | null {
  if (!pedido || pedido.cancelado || pedido.agendadoParaOutroDia) return null;
  if (pedido.aguardandoPagamentoOnline) return 'pix';
  if (pedido.aceito === false) return 'aceitar';

  switch (Number(pedido.statusOrdem)) {
    case EnumStatusPedido.Novo: return 'novo';
    case EnumStatusPedido.EmPreparacao: return 'preparo';
    case EnumStatusPedido.Pronto: return 'pronto';
    case EnumStatusPedido.SaiuParaEntrega: return 'rua';
    case EnumStatusPedido.Entregue: return !pedido.pago && atualizaFoiPago(pedido) ? 'receber' : null;
    default: return null;
  }
}

/** Atrasado é uma marca por cima da etapa, não uma etapa: a ação continua a da etapa. */
export function estaAtrasado(pedido: any, minutosTolerancia: number): boolean {
  return Number(pedido.statusOrdem) <= EnumStatusPedido.SaiuParaEntrega && (Number(pedido.duracao) || 0) > minutosTolerancia;
}

/** O próximo passo natural da etapa, só se o status estiver entre os permitidos; null = "Ver pedido". */
export function proximoPasso(pedido: any, etapa: EtapaComanda, podeAlterarStatus: boolean): PassoComanda | null {
  if (etapa === 'aceitar') return {tipo: 'aceitar', texto: 'Aceitar pedido', icone: 'fe-check'};
  if (etapa === 'receber') return podeAlterarStatus ? {tipo: 'pagar', texto: 'Recebi o pagamento', icone: 'fe-dollar-sign'} : null;
  if (etapa === 'pix') return null;

  const permitidos = statusPermitidos(pedido, podeAlterarStatus);
  const passo = (status: string, texto: string, icone: string): PassoComanda | null =>
    permitidos.includes(status) ? {tipo: 'status', status, texto, icone} : null;

  if (etapa === 'novo') return passo('Em preparação', 'Começar preparo', 'fe-play');
  if (etapa === 'preparo') return passo('Pronto', 'Pronto', 'fe-package');
  if (etapa === 'pronto') return ehEntrega(pedido) ? passo('Saiu para entrega', 'Saiu para entrega', 'fe-truck') : passo('Entregue', 'Entregue', 'fe-check');
  if (etapa === 'rua') return passo('Entregue', 'Entregue', 'fe-check');

  return null;
}

/** Ao ir para "Entregue": pergunta se recebeu quando a tela de hoje mostraria o "Foi pago?" desmarcado. */
export function perguntaPagamento(pedido: any): boolean {
  return atualizaFoiPago(pedido) && !pedido.pago;
}

export function abaDaEtapa(etapa: EtapaComanda): AbaComanda {
  if (etapa === 'novo' || etapa === 'preparo') return 'preparo';
  if (etapa === 'pronto') return 'prontos';
  return etapa as AbaComanda;
}

/** Atrasados primeiro; depois a ordem das etapas; dentro de cada uma, o mais antigo primeiro. */
export function ordeneParaOTrilho(pedidos: any[], minutosTolerancia: number): Array<{pedido: any, etapa: EtapaComanda, atrasado: boolean}> {
  return (pedidos || [])
    .map((pedido: any) => ({pedido, etapa: etapaDaComanda(pedido), atrasado: estaAtrasado(pedido, minutosTolerancia)}))
    .filter(item => item.etapa !== null)
    .sort((a, b) => (Number(b.atrasado) - Number(a.atrasado))
      || (ORDEM_DAS_ETAPAS.indexOf(a.etapa) - ORDEM_DAS_ETAPAS.indexOf(b.etapa))
      || ((Number(b.pedido.duracao) || 0) - (Number(a.pedido.duracao) || 0)));
}
